import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, Clock, ArrowUpRight, ArrowDownLeft, Users, DollarSign, Trash2,
  Bell, Mail, AlertCircle, Calculator, Plus, X, PieChart, TrendingUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { fundsAPI, paymentRemindersAPI, filesAPI } from '../../services/api';
import { toast } from 'sonner';

const FundsTab = ({
  funds,
  fundsWorkers,
  expenseCategories,
  transferData,
  setTransferData,
  setShowTransferModal,
  fetchFundsData
}) => {
  const [pendingSummary, setPendingSummary] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [showBudgetCalc, setShowBudgetCalc] = useState(false);
  const [budget, setBudget] = useState({
    // Income
    projected_bookings: 0,
    avg_booking_value: 150,
    other_income: 0,
    // Expenses
    payroll: 0,
    supplies: 0,
    fuel_transport: 0,
    marketing: 0,
    insurance: 0,
    equipment: 0,
    office: 0,
    other_expenses: 0
  });

  // Calculate totals
  const totalIncome = (budget.projected_bookings * budget.avg_booking_value) + budget.other_income;
  const totalExpenses = budget.payroll + budget.supplies + budget.fuel_transport + 
                        budget.marketing + budget.insurance + budget.equipment + 
                        budget.office + budget.other_expenses;
  const netProfit = totalIncome - totalExpenses;
  const profitMargin = totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : 0;

  useEffect(() => {
    // Fetch pending payments summary
    const fetchPendingSummary = async () => {
      try {
        const response = await paymentRemindersAPI.getPendingSummary();
        setPendingSummary(response.data);
      } catch (e) {
        console.error('Failed to fetch pending summary');
      }
    };
    fetchPendingSummary();
  }, []);

  const handleSendReminders = async (type = 'all') => {
    setSendingReminders(true);
    try {
      const response = await paymentRemindersAPI.sendReminders(type);
      toast.success(`${response.data.reminders_sent} payment reminders sent!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleDeleteTransaction = async (transaction) => {
    const reason = window.prompt('Reason for deleting this transaction (optional):');
    if (reason === null) return; // Cancelled
    try {
      await fundsAPI.deleteTransaction(transaction.id, reason || undefined);
      toast.success('Transaction deleted');
      fetchFundsData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Funds Management</h1>
        <div className="flex gap-2">
          <Button onClick={() => setShowBudgetCalc(true)} variant="outline" data-testid="budget-calculator-btn">
            <Calculator className="w-5 h-5 mr-2" />
            Budget Calculator
          </Button>
          <Button onClick={() => setShowTransferModal(true)} className="btn-primary" data-testid="new-expense-btn">
            <ArrowDownLeft className="w-5 h-5 mr-2" />
            Record Expense
          </Button>
        </div>
      </div>

      {/* Budget Calculator Modal */}
      <AnimatePresence>
        {showBudgetCalc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBudgetCalc(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="bg-[var(--background-paper)] rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-[var(--background-paper)] p-4 border-b flex items-center justify-between z-10">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Calculator className="w-6 h-6 text-[var(--primary)]" />
                  Budget Calculator
                </h2>
                <button onClick={() => setShowBudgetCalc(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6">
                {/* Results Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-[var(--success)]/10 rounded-xl text-center">
                    <p className="text-sm text-[var(--text-muted)]">Projected Income</p>
                    <p className="text-2xl font-bold text-[var(--success)]">${totalIncome.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-[var(--error)]/10 rounded-xl text-center">
                    <p className="text-sm text-[var(--text-muted)]">Total Expenses</p>
                    <p className="text-2xl font-bold text-[var(--error)]">${totalExpenses.toFixed(2)}</p>
                  </div>
                  <div className={`p-4 rounded-xl text-center ${netProfit >= 0 ? 'bg-[var(--primary)]/10' : 'bg-[var(--error)]/10'}`}>
                    <p className="text-sm text-[var(--text-muted)]">Net Profit</p>
                    <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-[var(--primary)]' : 'text-[var(--error)]'}`}>
                      ${netProfit.toFixed(2)}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-100 rounded-xl text-center">
                    <p className="text-sm text-[var(--text-muted)]">Profit Margin</p>
                    <p className="text-2xl font-bold text-purple-600">{profitMargin}%</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Income Section */}
                  <div>
                    <h3 className="font-semibold text-[var(--success)] flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5" />
                      Projected Income
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Expected # of Bookings</label>
                        <Input
                          type="number"
                          value={budget.projected_bookings}
                          onChange={(e) => setBudget({...budget, projected_bookings: parseFloat(e.target.value) || 0})}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Average Booking Value ($)</label>
                        <Input
                          type="number"
                          value={budget.avg_booking_value}
                          onChange={(e) => setBudget({...budget, avg_booking_value: parseFloat(e.target.value) || 0})}
                          placeholder="150"
                        />
                      </div>
                      <div className="p-3 bg-[var(--background-subtle)] rounded-lg">
                        <p className="text-sm text-[var(--text-muted)]">Booking Revenue</p>
                        <p className="font-bold text-lg">${(budget.projected_bookings * budget.avg_booking_value).toFixed(2)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Other Income ($)</label>
                        <Input
                          type="number"
                          value={budget.other_income}
                          onChange={(e) => setBudget({...budget, other_income: parseFloat(e.target.value) || 0})}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Expenses Section */}
                  <div>
                    <h3 className="font-semibold text-[var(--error)] flex items-center gap-2 mb-4">
                      <PieChart className="w-5 h-5" />
                      Operating Expenses
                    </h3>
                    <div className="space-y-3">
                      {[
                        { key: 'payroll', label: 'Payroll & Labor' },
                        { key: 'supplies', label: 'Operating Supplies' },
                        { key: 'fuel_transport', label: 'Fuel & Transportation' },
                        { key: 'marketing', label: 'Marketing & Ads' },
                        { key: 'insurance', label: 'Insurance' },
                        { key: 'equipment', label: 'Equipment & Tools' },
                        { key: 'office', label: 'Office & Admin' },
                        { key: 'other_expenses', label: 'Other Expenses' }
                      ].map(expense => (
                        <div key={expense.key} className="flex items-center gap-2">
                          <label className="text-sm w-36">{expense.label}</label>
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">$</span>
                            <Input
                              type="number"
                              value={budget[expense.key]}
                              onChange={(e) => setBudget({...budget, [expense.key]: parseFloat(e.target.value) || 0})}
                              className="pl-7"
                              placeholder="0"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Quick Tips */}
                <div className="mt-6 p-4 bg-[var(--primary)]/10 rounded-xl">
                  <h4 className="font-semibold text-[var(--primary)] mb-2">💡 Budget Tips</h4>
                  <ul className="text-sm text-[var(--text-secondary)] space-y-1">
                    <li>• Aim for a profit margin of at least 20-30%</li>
                    <li>• Labor typically accounts for 40-50% of service business expenses</li>
                    <li>• Set aside 10% of revenue for unexpected costs</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Balance Cards */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-[var(--success)]/15 rounded-xl">
              <Wallet className="w-6 h-6 text-[var(--success)]" />
            </div>
            <span className="text-[var(--text-muted)]">Available Balance</span>
          </div>
          <p className="text-3xl font-bold text-[var(--success)]">${funds.available_balance?.toFixed(2) || '0.00'}</p>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-amber-100 rounded-xl">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <span className="text-[var(--text-muted)]">Pending</span>
          </div>
          <p className="text-3xl font-bold text-amber-600">${funds.pending_balance?.toFixed(2) || '0.00'}</p>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-[#D4AF37]/10 rounded-xl">
              <ArrowUpRight className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <span className="text-[var(--text-muted)]">Total Received</span>
          </div>
          <p className="text-3xl font-bold text-[#D4AF37]">${funds.total_received?.toFixed(2) || '0.00'}</p>
        </div>
        
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-[var(--error)]/15 rounded-xl">
              <ArrowDownLeft className="w-6 h-6 text-[var(--error)]" />
            </div>
            <span className="text-[var(--text-muted)]">Total Expenses</span>
          </div>
          <p className="text-3xl font-bold text-[var(--error)]">${funds.total_expenses?.toFixed(2) || '0.00'}</p>
        </div>
      </div>

      {/* Payment Reminders Section */}
      {pendingSummary && pendingSummary.total_pending > 0 && (
        <div className="card p-6 mb-8 border-2 border-[var(--warning)]/30 bg-[var(--warning)]/10/50" data-testid="payment-reminders-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Bell className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="font-bold">Payment Reminders</h2>
                <p className="text-sm text-[var(--text-muted)]">
                  {pendingSummary.total_pending} pending payments totaling ${pendingSummary.total_amount_due?.toFixed(2)}
                </p>
              </div>
            </div>
            <Button
              onClick={() => handleSendReminders('all')}
              disabled={sendingReminders}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="send-all-reminders-btn"
            >
              {sendingReminders ? (
                <>
                  <Mail className="w-4 h-4 mr-2 animate-pulse" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send All Reminders
                </>
              )}
            </Button>
          </div>
          
          {/* Age-based Categories */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-[var(--background-paper)] rounded-lg border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-[var(--success)] font-medium">Under 24h</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSendReminders('24h')}
                  disabled={sendingReminders || pendingSummary.categories?.under_24h?.count === 0}
                  className="h-6 px-2 text-xs"
                >
                  <Mail className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-lg font-bold">{pendingSummary.categories?.under_24h?.count || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">${pendingSummary.categories?.under_24h?.amount?.toFixed(2) || '0.00'}</p>
            </div>
            
            <div className="p-3 bg-[var(--background-paper)] rounded-lg border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-amber-600 font-medium">24-48h</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSendReminders('48h')}
                  disabled={sendingReminders || pendingSummary.categories?.['24_to_48h']?.count === 0}
                  className="h-6 px-2 text-xs"
                >
                  <Mail className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-lg font-bold">{pendingSummary.categories?.['24_to_48h']?.count || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">${pendingSummary.categories?.['24_to_48h']?.amount?.toFixed(2) || '0.00'}</p>
            </div>
            
            <div className="p-3 bg-[var(--background-paper)] rounded-lg border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-orange-600 font-medium">48-72h</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSendReminders('72h')}
                  disabled={sendingReminders || pendingSummary.categories?.['48_to_72h']?.count === 0}
                  className="h-6 px-2 text-xs"
                >
                  <Mail className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-lg font-bold">{pendingSummary.categories?.['48_to_72h']?.count || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">${pendingSummary.categories?.['48_to_72h']?.amount?.toFixed(2) || '0.00'}</p>
            </div>
            
            <div className="p-3 bg-[var(--background-paper)] rounded-lg border border-[var(--error)]/30">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-[var(--error)] font-medium flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Over 72h
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSendReminders('all')}
                  disabled={sendingReminders || pendingSummary.categories?.over_72h?.count === 0}
                  className="h-6 px-2 text-xs text-[var(--error)]"
                >
                  <Mail className="w-3 h-3" />
                </Button>
              </div>
              <p className="text-lg font-bold text-[var(--error)]">{pendingSummary.categories?.over_72h?.count || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">${pendingSummary.categories?.over_72h?.amount?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Expense Actions */}
      <div className="card p-6 mb-8">
        <h2 className="font-bold mb-4">Quick Actions - Move Funds</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {expenseCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => {
                setTransferData({ ...transferData, category: cat.id });
                setShowTransferModal(true);
              }}
              className="flex flex-col items-center gap-2 p-4 border rounded-xl hover:bg-[var(--accent)] transition-colors"
            >
              <cat.icon className="w-6 h-6 text-[var(--primary)]" />
              <span className="text-sm font-medium text-center">{cat.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Worker Payments Budget Section */}
      <div className="card p-6 mb-8" data-testid="worker-payments-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#F4C2C2]/15 rounded-lg">
              <Users className="w-6 h-6 text-[#F4C2C2]" />
            </div>
            <div>
              <h2 className="font-bold">Worker Payments</h2>
              <p className="text-sm text-[var(--text-muted)]">Track payments to each worker against your budget</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center px-4 py-2 bg-[var(--success)]/10 rounded-lg">
              <p className="font-bold text-[var(--success)]">${fundsWorkers.summary?.total_paid_to_workers?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-green-700">Total Paid</p>
            </div>
            <div className="text-center px-4 py-2 bg-[var(--warning)]/10 rounded-lg">
              <p className="font-bold text-amber-600">${fundsWorkers.summary?.total_pending_payroll?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-amber-700">Pending</p>
            </div>
          </div>
        </div>
        
        {fundsWorkers.workers?.length === 0 ? (
          <div className="text-center text-[var(--text-muted)] py-8">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No worker payments recorded yet</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {fundsWorkers.workers?.map((worker) => (
              <div 
                key={worker.id} 
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-[var(--background-subtle)]"
                data-testid={`worker-payment-${worker.id}`}
              >
                <div className="flex items-center gap-3">
                  {worker.profile_picture ? (
                    <img 
                      src={filesAPI.getUrl(worker.profile_picture)} 
                      alt={worker.full_name} 
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#F4C2C2]/15 flex items-center justify-center">
                      <span className="text-[#F4C2C2] font-medium">
                        {worker.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{worker.full_name}</p>
                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                      <span>${worker.hourly_rate || 0}/hr</span>
                      <span>•</span>
                      <span>{worker.paid_stubs_count || 0} pay stubs</span>
                      {worker.last_payment_date && (
                        <>
                          <span>•</span>
                          <span>Last: {new Date(worker.last_payment_date).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {worker.pending_amount > 0 && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-amber-600">${worker.pending_amount.toFixed(2)}</p>
                      <p className="text-xs text-amber-500">Pending</p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="font-bold text-[var(--success)]">${worker.total_paid.toFixed(2)}</p>
                    <p className="text-xs text-[var(--text-muted)]">Total Paid</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setTransferData({
                        ...transferData,
                        category: 'payroll',
                        recipient: worker.full_name,
                        description: `Payment to ${worker.full_name}`
                      });
                      setShowTransferModal(true);
                    }}
                    className="text-[var(--primary)] border-[var(--primary)]"
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    Pay
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Recent Transactions */}
      <div className="card p-6">
        <h2 className="font-bold mb-4">Recent Transactions</h2>
        {funds.transactions?.length === 0 ? (
          <p className="text-center text-[var(--text-muted)] py-8">No transactions yet</p>
        ) : (
          <div className="space-y-3">
            {funds.transactions?.map((t, idx) => (
              <div key={t.id || idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-[var(--background-subtle)]">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${t.type === 'income' ? 'bg-[var(--success)]/15' : 'bg-[var(--error)]/15'}`}>
                    {t.type === 'income' ? (
                      <ArrowUpRight className="w-5 h-5 text-[var(--success)]" />
                    ) : (
                      <ArrowDownLeft className="w-5 h-5 text-[var(--error)]" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{t.description || t.category}</p>
                    <p className="text-xs text-[var(--text-muted)]">
                      {new Date(t.created_at).toLocaleDateString()}
                      {t.recipient && ` • ${t.recipient}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-bold ${t.type === 'income' ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                    {t.type === 'income' ? '+' : '-'}${t.amount?.toFixed(2)}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[var(--error)] hover:text-red-700 hover:bg-[var(--error)]/10"
                    onClick={() => handleDeleteTransaction(t)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default FundsTab;
